/**
 * Browser-side OpenMarkets client. greenbook is pure-static: signed in, the browser talks
 * straight to the Flow API with the user's own key (CORS is fully open — verified:
 * `access-control-allow-origin: *`, and the preflight reflects our headers); the key rides in
 * `X-API-Key`. Signed out, the same calls are rewritten onto the same-origin public feed
 * (`/api/public/*`, an org-keyed Pages Function) with no key at all.
 *
 * We keep ONE {@link OpenMarketsClient} for the app's lifetime (the key is resolved per
 * request, see getClient) so its rate-limit state is shared across all calls. Rate-limit headers from every
 * response are pushed into the status store so the top-bar budget meter keeps working.
 *
 * Base URLs default to production and can be overridden at build time with
 * `VITE_OPENMARKETS_BASE` / `VITE_OPENMARKETS_STREAM` (e.g. a staging deploy).
 */
import { OpenMarketsClient } from "@greenbook/client";
import type { Result } from "@greenbook/client";
import { resolveApiKey } from "./omauth.js";
import { pushRateLimit } from "./api.js";
import { PUBLIC_UNAVAILABLE, SIGN_IN_REQUIRED, toPublicUrl } from "./publicFeed.js";

// VITE_OPENMARKETS_BASE is the API ROOT (shared with the sign-in SDK's apiBase); the Flow
// REST client needs the `/flow/v1` base, so append it unless it's already there. Unset → the
// client's own production default (`…/flow/v1`).
function flowBase(): string | undefined {
  const raw = import.meta.env.VITE_OPENMARKETS_BASE?.replace(/\/+$/, "");
  if (!raw) return undefined;
  return raw.endsWith("/flow/v1") ? raw : `${raw}/flow/v1`;
}
const BASE = flowBase();
export const STREAM_URL =
  import.meta.env.VITE_OPENMARKETS_STREAM || "wss://api.openmarkets.ai/flow/v1/stream";

let singleton: OpenMarketsClient | null = null;

/**
 * ONE client for the app's lifetime. It never stores a real key: a fetch wrapper resolves a
 * FRESH key (SDK `getApiKey()` — auto-renewing — or the manual pasted key) per request and
 * sets `X-API-Key`, overriding the placeholder the client would otherwise send. This is what
 * makes the 7-day key expiry invisible: every request carries a current, possibly-just-renewed
 * key.
 *
 * No key (signed out) → the request is rewritten onto the same-origin public feed
 * (`/api/public/<same path>`, see lib/publicFeed.ts) and sent with NO key. See
 * {@link signedOutFetch} for how an off-list path or a missing feed surfaces.
 */
/**
 * The abort signal of the request being started right now. The typed client's resource methods
 * take no signal, but each one reaches the fetch wrapper synchronously, so {@link withSignal}
 * parks the caller's (react-query's) signal here for that synchronous start. A cancelled query
 * then really stops downloading — before this, a "cancelled" ~3 MB liquidity fetch ran to
 * completion beside its replacement.
 */
let ambientSignal: AbortSignal | undefined;

/** Run `start` with `signal` attached to any request it starts synchronously. */
export function withSignal<T>(signal: AbortSignal | undefined, start: () => Promise<T>): Promise<T> {
  const prev = ambientSignal;
  ambientSignal = signal;
  try {
    return start();
  } finally {
    ambientSignal = prev;
  }
}

export function getClient(): OpenMarketsClient {
  if (singleton) return singleton;
  singleton = new OpenMarketsClient({
    apiKey: "sdk-managed", // placeholder; the fetch wrapper injects the real key per request
    baseUrl: BASE,
    fetch: async (url, init) => {
      // Read before the first await: the caller's signal is only ambient while the request starts.
      const signal = init?.signal ?? ambientSignal;
      if (signal) init = { ...init, signal };
      const key = await resolveApiKey();
      if (!key) return signedOutFetch(String(url), init);
      // Bind fetch to window (unbound globalThis.fetch throws "Illegal invocation" in browsers)
      // and override X-API-Key with the freshly-resolved key.
      const headers = new Headers(init?.headers);
      headers.set("X-API-Key", key);
      return window.fetch(url, { ...init, headers });
    },
    validate: "warn",
    onRateLimit: (rl) => pushRateLimit(rl),
    onSchemaMismatch: (endpoint, issues) =>
      console.warn(`[greenbook] schema drift on ${endpoint}:`, issues.map((i) => i.path.join(".")).join(", ")),
  });
  return singleton;
}

// Error codes the signed-out path produces itself (never an API rejection of a credential).
// They live in the leaf publicFeed module: api.ts imports them, and api.ts ⇄ client.ts is a cycle.
export { PUBLIC_UNAVAILABLE, SIGN_IN_REQUIRED };

/**
 * A synthetic JSON error Response. Returned (not thrown) from the fetch wrapper on purpose:
 * the HTTP core retries anything the fetch THROWS (and 429/500/503 responses) — a 401/424 is
 * terminal, so "sign in to see this" surfaces immediately instead of after three backoffs.
 */
function syntheticError(status: number, code: string, message: string): Response {
  return new Response(JSON.stringify({ error: { code, message } }), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/** Once the feed has said it's not configured here, stop asking for this page load. */
let publicFeedDown = false;

/**
 * Signed-out transport. Public-eligible GETs go to `/api/public/<path>` with no key; anything
 * else answers a synthetic 401 `sign_in_required`. If the feed isn't there — the function
 * answers 503 `public_unavailable`, or a static host / the dev server without a key answers
 * the SPA's index.html (or any non-JSON) — it answers a synthetic 424 `public_unavailable`.
 * Both codes are mapped to "Sign in to see this" by the UI and never sign anyone out.
 */
export async function signedOutFetch(
  url: string,
  init: RequestInit | undefined,
  doFetch: (input: string, init?: RequestInit) => Promise<Response> = (i, o) => window.fetch(i, o),
): Promise<Response> {
  const target = toPublicUrl(url, init?.method ?? "GET");
  if (!target) return syntheticError(401, SIGN_IN_REQUIRED, "Sign in to see this.");
  if (publicFeedDown) return syntheticError(424, PUBLIC_UNAVAILABLE, "The public market feed isn't available here.");

  // No credential header at all — not even the client's placeholder.
  const headers = new Headers(init?.headers);
  headers.delete("X-API-Key");
  headers.set("Accept", "application/json");
  const res = await doFetch(target, { ...init, headers });

  const isJson = (res.headers.get("content-type") ?? "").includes("json");
  if (!isJson) {
    publicFeedDown = true;
    return syntheticError(424, PUBLIC_UNAVAILABLE, "The public market feed isn't available here.");
  }
  if (res.status === 503) {
    const code = await res
      .clone()
      .json()
      .then((b: { error?: { code?: string } }) => b?.error?.code)
      .catch(() => undefined);
    if (code === PUBLIC_UNAVAILABLE) {
      publicFeedDown = true;
      return syntheticError(424, PUBLIC_UNAVAILABLE, "The public market feed isn't configured.");
    }
  }
  return res;
}

/** Tests only: forget a cached "feed is down". */
export function resetPublicFeedState(): void {
  publicFeedDown = false;
}

/** Thrown when a call is attempted with no connected key (legacy; kept for the error mapper). */
export class NoCredentialError extends Error {
  constructor() {
    super("No OpenMarkets key connected.");
    this.name = "NoCredentialError";
  }
}

const POSITIONS_PAGE = 200;
const POSITIONS_MAX_PAGES = 80; // ~16k rows; the live book is ~10.8k (see FRICTION.md)

/**
 * Page through the positions book into one `{ om_account_id, positions }` rollup. Ported
 * from the old server store. `open`/`settled` are cheap server-side slices; omitting
 * `status` walks the ENTIRE lifetime book (~10.8k rows, ~44s cold) — react-query caches it,
 * but the first uncached read of the unfiltered view will block. The desk's holdings view
 * uses the `open` slice, so this cold walk only bites the dedicated "all positions" page.
 */
export async function fetchAllPositions(status?: string): Promise<Result<unknown>> {
  const client = getClient();
  let cursor: string | undefined;
  let omAccountId: string | undefined;
  const all: unknown[] = [];
  let last: Result<unknown> | null = null;
  for (let i = 0; i < POSITIONS_MAX_PAGES; i++) {
    const res = await client.account.positions({ status, limit: POSITIONS_PAGE, cursor });
    last = res as Result<unknown>;
    const data = res.data as { om_account_id?: string; positions?: unknown[] };
    if (data.om_account_id) omAccountId = data.om_account_id;
    if (Array.isArray(data.positions)) all.push(...data.positions);
    const next = res.pagination?.has_more ? res.pagination.next_cursor : null;
    if (!next) break;
    cursor = next;
  }
  return {
    data: { om_account_id: omAccountId, positions: all },
    rateLimit: last?.rateLimit ?? null,
  };
}
